import ReactTooltip from "react-tooltip";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { actionError, actionSuccess, showLoader, hideLoader } from "middleware/actions/utilityAction";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as Yup from "yup";
import { TextField, TextEditor } from "app/components/CustomFormElements/syncFusionFormFields";
import { addDocumentValidationSchema, editDocumentValidationSchema } from "app/helper/validationSchema/companyValidationSchema";
import { addCompanyDocument, editCompanyDocument, fetchCompanyDocumentUrl, fetchCompanyDocumentsByCompanyId } from "middleware/services/companyApi";
import { ckEditorMinimalConfig } from "../../../../../../../config/ckEditorConfig";
import { useHistory } from "react-router-dom";
import { formattedDate } from "utils";
import { setBreadCrumb } from "middleware/actions/breadCrumbAction";
import { fetchSingleCompany } from "middleware/services/companyApi";

const UploadComponent = (props) => {
  const dispatch = useDispatch();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { acceptedFilesFormat: [".pdf,.doc,.docs,.docx,.xlsx,.xls,.pptx,.txt"] },
    multiple: false,
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        const reason = fileRejections[0]?.errors[0]?.code;
        let errorMsg = "Invalid file";

        if (reason === "file-invalid-type") {
          errorMsg = "Unsupported file type";
        } else if (reason === "file-too-large") {
          errorMsg = "File exceeds maximum allowed size of 10MB";
        }

        dispatch(actionError(errorMsg));

        props.formik.setTouched({ ...props.formik.touched, documentFile: true });
        props.formik.setFieldError("documentFile", "Invalid file type or size");
        props.setDocumentError("Invalid file type or size");
      } else {
        props.setDocumentError("");

        const currentFiles = props.formik.values.documentFile || [];
        const newFileName = acceptedFiles[0]?.name;

        if (!currentFiles.length || currentFiles[0]?.name !== newFileName) {
          props.formik.setValues({
            ...props.formik.values,
            documentFile: acceptedFiles,
          });
        }
      }
    },
  });

  const fileIcon = {
    pdf: "pdf.svg",
    doc: "doc-icon.svg",
    docx: "doc-icon.svg",
    xlsx: "/pngs/empty-excel.png",
    xls: "/pngs/empty-excel.png",
    csv: "/pngs/CSV-file-icon.png",
    pptx: "ppt.svg",
    txt: "txtfile-icon.svg",
  };

  return (
    <>
      <p className="mb-1" style={{ fontSize: "1rem", fontWeight: "600", color: "#000" }}>
        Upload Document *
      </p>
      <div className="d-flex justify-content-between">
        <div className="form-group border rounded w-25" {...getRootProps()}>
          <input {...getInputProps()} />
          <button type="button" className="btn btn-grey w-100 py-2" style={{ fontSize: ".8rem", color: "#4B4B55" }}>
            <img src="/assets/upload-icon.svg" alt="upload-file" style={{ marginRight: "1rem" }} />
            <span>
              Drag and Drop or <b>Browse</b>
            </span>
          </button>
        </div>
        <div className="w-50">
          {props.formik.values.documentFile &&
            props.formik.values.documentFile.map((file, i) => (
              <div key={i} className="d-flex justify-content-between align-items-center border rounded p-2">
                <div className="d-flex justify-content-start" style={{ gap: ".5rem" }}>
                  <div className="me-1">
                    <img src={`/assets/${fileIcon[file.name.split(".").pop().toLowerCase()]}`} alt="file" style={{ height: "2rem" }} />
                  </div>
                  <div className="me-1">
                    <p className="m-0 p-0" style={{ fontSize: ".8rem", color: "#000", lineHeight: 1 }}>
                      {file.name.includes(".") ? file.name.substring(0, file.name.lastIndexOf(".")) : file.name}
                    </p>
                    <span className="m-0" style={{ fontSize: ".7rem", color: "#777", lineHeight: 1 }}>
                      {formattedDate(file.lastModifiedDate)}
                    </span>
                  </div>
                </div>
                <div>
                  <img
                    className="cursor-pointer"
                    src="/assets/trash-box.svg"
                    alt="trash-box"
                    onClick={() => props.formik.setFieldValue("documentFile", [])}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
      <small style={{}}>
        <strong>Note: </strong>
        <ul className="" style={{ marginLeft: "1.5rem" }}>
          <li>Accepts only pdf, doc, docs, xlsx, xls, csv, pptx, txt files.</li>
          <li>Maximum file size allowed for upload: 10 MB</li>
        </ul>
      </small>
      <div className="text-danger" style={{ display: props.formik.touched.documentFile === true ? "block" : "none" }} role="alert">
        <small>{props.formik.touched.documentFile === true ? props.documentError || "" : ""}</small>
      </div>
      {props?.editData ? (
        <>
          <br />
          <a href={fetchCompanyDocumentUrl(props.formik.values.linkFile, props.formik.values.documentFileName)}>
            <img src="/assets/download-icon.svg" alt="image-download" /> {props.formik.values.documentFileName}
          </a>
          <ReactTooltip />
        </>
      ) : (
        ""
      )}
    </>
  );
};

const CompanyBasicDocument = (props) => {
  const dispatch = useDispatch();

  const { companyId, documentId } = props.computedMatch?.params || {};
  const lastSetValues = useRef(null);
  const [companyName, setCompanyName] = useState("");
  const [documentError, setDocumentError] = useState("");

  const split_url = props.location?.pathname.split("/");
  const backPath = props?.editData ? `${split_url.slice(0, -3).join("/")}/edit#basic` : `${split_url.slice(0, -2).join("/")}/edit#basic`;
  const history = useHistory();

  const initialFormValues = {
    documentTitle: "",
    documentDescription: "",
    linkFile: "",
    documentFile: "",
    documentFileName: "",
  };

  const breadCrumbLinks = [
    { linkUrl: "/company", linkName: `Company`, linkActive: false },
    { linkUrl: `/company/${companyId}/edit#basic`, linkName: companyName, linkActive: false },
    { linkUrl: `/company/${companyId}/edit#basic`, linkName: "Basic", linkActive: false },
    { linkUrl: ``, linkName: "Documents", linkActive: true },
  ];

  const getCompanyName = async () => {
    if (companyId) {
      const fields = { fields: ["id", "companyName"] };
      const response = await fetchSingleCompany(companyId, { ...fields });
      setCompanyName(response.data.data.companyName);
    }
  };

  const formik = useFormik({
    initialValues: {
      ...initialFormValues,
    },
    validationSchema: Yup.object().shape(props.editData ? editDocumentValidationSchema : addDocumentValidationSchema),
  });
  const setFormikValues = useCallback(
    (data) => {
      const newData = {
        documentTitle: data.docTitle ? data.docTitle : "",
        documentDescription: data.docDescription || "",
        linkFile: data.docFileName || "",
        documentFile: "",
        documentFileName: data.docName || "",
      };

      if (JSON.stringify(lastSetValues.current) !== JSON.stringify(newData)) {
        formik.setValues(newData);
        lastSetValues.current = newData;
      }
    },
    [formik]
  );

  const onUpdateData = async (data) => {
    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }
    try {
      dispatch(showLoader());
      const response = await editCompanyDocument(companyId, documentId, formData);
      if (response.status === 200) {
        dispatch(hideLoader());
        dispatch(actionSuccess("Product document updated successfully"));
        history.push(backPath);
      } else {
        dispatch(hideLoader());
        dispatch(actionError("Something went wrong"));
      }
    } catch (err) {
      dispatch(hideLoader());
      dispatch(actionError(err ? `${err.status}:${err.statusText}` : "Something went wrong"));
    }
  };

  const onSaveData = async (data) => {
    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }

    try {
      dispatch(showLoader());
      const response = await addCompanyDocument(companyId, formData);
      if (response.status === 200) {
        dispatch(hideLoader());
        dispatch(actionSuccess("Product document saved successfully"));
        return { status: "success" };
      } else {
        dispatch(hideLoader());
        dispatch(actionError("Something went wrong"));
      }
    } catch (err) {
      dispatch(hideLoader());
      dispatch(actionError(err ? `${err.status}:${err.statusText}` : "Something went wrong"));
    }
  };

  const saveDocumentData = async () => {
    const { values } = formik;
    const productDocumentData = {
      docTitle: values.documentTitle,
      docDescription: values.documentDescription,
      file: values.documentFile[0],
    };
    if (props.editData) {
      if (!values.documentFile[0]) delete productDocumentData.file;
    }
    const response = props?.editData ? onUpdateData(productDocumentData) : onSaveData(productDocumentData);
    response.then((result) => {
      history.push(backPath);
    });
  };

  const handleSubmitDocument = () => {
    formik.validateForm().then((res) => {
      if (Object.keys(res).length) {
        const touched = {};
        Object.keys(res).forEach((field) => {
          touched[field] = true;
        });
        formik.setFormikState({ ...formik, touched: touched, errors: res });
      } else {
        saveDocumentData();
      }
    });
  };

  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));
  }, [breadCrumbLinks]);

  useEffect(() => {
    if (props.editData && companyId && documentId) {
      formik.resetForm();
      fetchCompanyDocumentsByCompanyId(companyId)
        .then((res) => {
          setFormikValues(res.data?.data?.companyDocuments?.filter((e) => e.id === documentId)?.[0] || {});
        })
        .catch((err) => {
          dispatch(actionError(err?.data?.message || "Something went wrong"));
        });
    } else {
      formik.resetForm();
    }
  }, [props.editData, companyId, documentId]);

  useEffect(() => {
    getCompanyName();
  }, [companyId]);

  return (
    <div className="p-0 m-3">
      <div className="p-2 border custom-bg-grey text-white rounded-top">Company - Documents</div>
      <div className="d-flex flex-column justify-content-between border border-top-0" style={{ height: "82vh" }}>
        <form className="p-4">
          <div className="mb-4">
            <div style={{ width: "40%" }}>
              <TextField formik={formik} id="documentTitle" name="documentTitle" placeholder="Name *" />
            </div>
            <div className="d-flex flex-row col-12 m-0 p-0">
              <div className="col-6 px-0" style={{ marginRight: "1rem" }}>
                <TextEditor
                  formik={formik}
                  maxLength={1000}
                  properties={{
                    id: "documentDescription",
                    name: "documentDescription",
                    activeClass: "documentDescription",
                    label: "Description *",
                    config: { ...ckEditorMinimalConfig, editorplaceholder: "Description *", height: "200px" },
                  }}
                />
              </div>

              <div className="col-6">
                <UploadComponent editData={props.editData} formik={formik} documentError={documentError} setDocumentError={setDocumentError} />
              </div>
            </div>
          </div>
        </form>
        <div className="text-right border-top p-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              history.push(backPath);
            }}
            className="btn btn-outline-dark mr-3"
          >
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmitDocument} disabled={false}>
            {props?.editData ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyBasicDocument;
